import { RevisionLicenseConclusionScope } from '../model-typings/revisionlicenseconclusion';
import type { RevisionLicense } from '../model-typings';

import type { PaginatedRequestQuery } from './routes/pagination';

/**
 * How a license's copyleft obligations propagate to consuming works.
 * See modules/licenses/README.md ("Copyleft Classification") for definitions and sources.
 * - 'none': no copyleft (permissive, proprietary, public-domain)
 * - 'file': obligations attach only to files/modules containing licensed code (MPL, CDDL, EPL)
 * - 'library': the library stays copyleft, but consumers may keep their own terms when the
 *   library remains user-replaceable — typically via dynamic linking (LGPL)
 * - 'strong': the whole combined/derivative work must adopt the license on distribution (GPL)
 * - 'network': strong, and offering the work as a network service also triggers source
 *   obligations (AGPL, SSPL)
 * - 'linking-exception': strong-based license whose exception frees consuming works at any
 *   linkage, static included; the licensed code itself stays copyleft (GPL w/ classpath or
 *   GCC runtime exception).
 */
export type CopyleftType = 'none' | 'file' | 'library' | 'strong' | 'network' | 'linking-exception';

export type License = {
  // Required Fields
  name: string;
  licenseId: string; // spdx license id
  licenseText: string;

  // Scanning Fields
  /**
   * scanner signatures that map to this license
   */
  matches?: string[];

  // Report Metadata
  standardLicenseTemplate?: string;
  standardLicenseHeader?: string;
  standardLicenseHeaderTemplate?: string;

  // Other Metadata
  reference?: string;
  isDeprecatedLicenseId?: boolean;
  /**
   * Replacement license ID to use when the license is deprecated
   */
  replacementLicenseId?: string;
  /**
   * URL to the license details
   */
  detailsUrl?: string;
  referenceNumber?: string;
  seeAlso?: string[];
  isOsiApproved?: boolean;
  isFsfLibre?: boolean;
  licenseExceptionId?: string;
  licenseComments?: string;
  /**
   * Absent means "not yet classified" — only an explicit 'none' asserts the license
   * was reviewed and has no copyleft.
   */
  copyleftType?: CopyleftType;
};

export type DependencyLicense = License & {
  copyright: string | null;
  isCorrected: boolean;
};

export type LicenseTermId =
  | 'Include License'
  | 'Commercial Use'
  | 'Hold Liable'
  | 'Modify'
  | 'Distribute'
  | 'Include Copyright'
  | 'Sublicense'
  | 'Private Use'
  | 'Disclose Source'
  | 'State Changes'
  | 'Place Warranty'
  | 'Include Original'
  | 'Include Install Instructions'
  | 'Use Patent Claims'
  | 'Include Notice'
  | 'Rename'
  | 'Use Trademark'
  | 'Contact Author'
  | 'Relicense'
  | 'Give Credit'
  | 'Statically Link'
  | 'Compensate for Damages'
  | 'Pay Above Use Threshold';

export type LicenseTerm = {
  licenseId: string;
  termId: LicenseTermId;

  custom_description: string | null;
  role: 'CAN' | 'CANNOT' | 'MUST';
  createdAt: string;
  updatedAt: string;
};

export type LicenseSummary = {
  id: string;
  title: string;
  slug: string;
  shorthand: string | null;
  fulltext: string | null;
  summary: string | null;

  permissiveness_score: number | null;
  tags: string[] | null;

  spdx_id?: string;
  RevisionLicense?: RevisionLicense;
  depths?: number[];
  paths?: string[];
  Terms?: LicenseTerm[];
  createdAt: string;
  updatedAt: string;
};

export type UpdateProjectCorrectionRequestLicense = {
  licenseId: string;
  ignored?: boolean;
  title?: string;
  url?: string;
  text?: string;
  copyright?: string;
  licenseGroupId?: number;
};

export type ReadLicenseAPIResponse = License & {
  Summary?: LicenseSummary;
};

export type ReadDependencyLicenseAPIResponse = DependencyLicense & {
  Summary?: LicenseSummary;
};

type ProjectConclusionRequestScope = {
  scope: RevisionLicenseConclusionScope.PROJECT;
  projectLocator: string;
};

type RevisionConclusionRequestScope = {
  scope: RevisionLicenseConclusionScope.REVISION;
  projectLocator: string;
  revisionLocator: string;
};

type ReleaseGroupConclusionRequestScope = {
  scope: RevisionLicenseConclusionScope.RELEASE_GROUP;
  releaseGroupId: number;
};

type ReleaseConclusionRequestScope = {
  scope: RevisionLicenseConclusionScope.RELEASE;
  releaseGroupId: number;
  releaseId: number;
};

type OrganizationConclusionRequestScope = {
  scope: RevisionLicenseConclusionScope.ORGANIZATION;
  organizationId: number;
};

type GlobalConclusionRequestScope = {
  scope: RevisionLicenseConclusionScope.GLOBAL;
};

/**
 * The scopes an organization-wide conclusion sits under: a conclusion at any of these outranks it.
 */
export type LocalConclusionRequestScope =
  | ProjectConclusionRequestScope
  | RevisionConclusionRequestScope
  | ReleaseGroupConclusionRequestScope
  | ReleaseConclusionRequestScope;

export type ConclusionRequestScope =
  | LocalConclusionRequestScope
  | OrganizationConclusionRequestScope
  | GlobalConclusionRequestScope;

// Type guard functions for ConclusionRequestScope
export const isProjectScope = (scope: ConclusionRequestScope): scope is ProjectConclusionRequestScope => {
  return scope.scope === RevisionLicenseConclusionScope.PROJECT;
};

export const isRevisionScope = (scope: ConclusionRequestScope): scope is RevisionConclusionRequestScope => {
  return scope.scope === RevisionLicenseConclusionScope.REVISION;
};

export const isReleaseGroupScope = (scope: ConclusionRequestScope): scope is ReleaseGroupConclusionRequestScope => {
  return scope.scope === RevisionLicenseConclusionScope.RELEASE_GROUP;
};

export const isReleaseScope = (scope: ConclusionRequestScope): scope is ReleaseConclusionRequestScope => {
  return scope.scope === RevisionLicenseConclusionScope.RELEASE;
};

export const isOrganizationScope = (scope: ConclusionRequestScope): scope is OrganizationConclusionRequestScope => {
  return scope.scope === RevisionLicenseConclusionScope.ORGANIZATION;
};

export const isGlobalScope = (scope: ConclusionRequestScope): scope is GlobalConclusionRequestScope => {
  return scope.scope === RevisionLicenseConclusionScope.GLOBAL;
};

export type CorrectLicenseRequest = {
  data: Omit<UpdateProjectCorrectionRequestLicense, 'licenseId'>;
  parentRevisionLocator?: string;
}

export type UpdateLicenseResponse = {
  result: RevisionLicense | null,
}

export type AddLicenseToProjectCorrectionRequest = {
  data: UpdateProjectCorrectionRequestLicense;
  parentRevisionLocator?: string;
  licenseGroupId?: number;
};

/**
 * This shows both the base, auto-concluded licenses, as well as any global overrides.
 */

export type GlobalConclusionResult = {
  baseConcludedLicenses: string[];
  globalConcludedLicenses: string[];
};

// The result is intentionally structured this way to allow for extending the scoped and base conclusions separately.
export type ConclusionData = {
  scoped: {
    licenses: string[];
    lastEditedBy: string | null;
    updatedAt: Date | null;
    scope?: RevisionLicenseConclusionScope | null;
    /**
     * True when the organization has its own conclusion for this revision, whether or not it is the one that won
     * here. Correction snapshots (version-less organization rows) do not count. Only set for project, revision,
     * release group and release reads.
     */
    hasOrganizationConclusion?: boolean;
  };
  base: {
    licenses: string[];
    justification: string | null;
  };
};

/**
 * The key is the dependency revision locator, and the value is the concluded license.
 * If a concluded license for a dependency revision locator is not found, the value is an empty array.
 */
export type ConclusionResult = Record<string, ConclusionData>;

export type CustomLicenseResponse = {
  scope: string | null;
  dependencyProjectLocator: string;
  dependencyRevisionLocator: string;
  projectLocator: string;
  projectRevision: string;
  title: string | null;
  description: string | null;
  licenseTitle: string;
  url: string | null;
  licenseId: string;
  copyright: string | null;
  text: string | null;
  correctedAt: Date;
};

export const emptyConclusionData: ConclusionData = {
  scoped: { licenses: [], lastEditedBy: null, updatedAt: null },
  base: { licenses: [], justification: null },
};

export type ListLicensesPostRequestBody = {
  licenses?: string[],
};

export type ListLicensesAPIResponse = License[];

export type ListLicensesAPIRequest = {
  $title?: string,
  licenses?: string[],
  onlyIds?: string,
  idsAndMatches?: string,
} & PaginatedRequestQuery;
